import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, RotateCcw, AlertTriangle, HelpCircle } from "lucide-react";
import GameInstructionsModal from "../ui/GameInstructionsModal";

const TeamGame = ({ id = "synergy" }) => {
  const canvasRef = useRef(null);
  const gameContainerRef = useRef(null);
  const [gameState, setGameState] = useState("start"); // start, playing, gameOver
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const requestRef = useRef();

  // Game Constants
  const GRAVITY = 0.25;
  const JUMP = -4.5;
  const OBSTACLE_SPEED = 3.5;
  const OBSTACLE_SPAWN_RATE = 100;
  const PLANE_SIZE = 40;

  const planeRef = useRef({ y: 200, velocity: 0, rotation: 0 });
  const obstaclesRef = useRef([]);
  const frameCountRef = useRef(0);
  const particlesRef = useRef([]);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const canvasWidth = useRef(800);
  const canvasHeight = useRef(500);

  useEffect(() => {
    const saved = localStorage.getItem("meraki_team_high_score");
    if (saved) setHighScore(parseInt(saved));
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    
    const updateSize = () => {
      if (gameContainerRef.current && canvasRef.current) {
        const { clientWidth, clientHeight } = gameContainerRef.current;
        canvasWidth.current = clientWidth * window.devicePixelRatio;
        canvasHeight.current = clientHeight * window.devicePixelRatio;
        canvasRef.current.width = canvasWidth.current;
        canvasRef.current.height = canvasHeight.current;
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const initGame = useCallback(() => {
    planeRef.current = { y: 200, velocity: 0, rotation: 0 };
    obstaclesRef.current = [];
    frameCountRef.current = 0;
    particlesRef.current = [];
    setScore(0);
    setGameState("playing");
  }, []);

  const handleJump = useCallback(() => {
    if (gameState === "playing") planeRef.current.velocity = JUMP;
    else if (gameState === "start" || gameState === "gameOver") initGame();
  }, [gameState, JUMP, initGame]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || gameState !== "playing") return;
    const ctx = canvas.getContext("2d");

    const loop = () => {
      const p = planeRef.current;
      p.velocity += GRAVITY;
      p.y += p.velocity;
      p.rotation = Math.min(Math.PI / 4, Math.max(-Math.PI / 4, p.velocity * 0.1));

      frameCountRef.current++;
      if (frameCountRef.current % OBSTACLE_SPAWN_RATE === 0) {
        const gap = 160;
        const h = 50 + Math.random() * (canvasHeight.current - gap - 100);
        obstaclesRef.current.push({ x: canvasWidth.current, top: h, bottom: canvasHeight.current - h - gap, passed: false });
      }

      obstaclesRef.current.forEach(obs => {
        obs.x -= OBSTACLE_SPEED;
        if (!obs.passed && obs.x < 50) { obs.passed = true; setScore(s => s + 1); }
        if (50 < obs.x + 60 && 50 + PLANE_SIZE > obs.x && (p.y < obs.top || p.y + PLANE_SIZE > canvasHeight.current - obs.bottom)) setGameState("gameOver");
      });

      if (p.y < 0 || p.y + PLANE_SIZE > canvasHeight.current) setGameState("gameOver");
      obstaclesRef.current = obstaclesRef.current.filter(obs => obs.x > -100);

      // Draw
      ctx.clearRect(0, 0, canvasWidth.current, canvasHeight.current);
      ctx.strokeStyle = "rgba(201,169,97,0.1)";
      for(let x=0; x<canvasWidth.current; x+=40) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,canvasHeight.current); ctx.stroke(); }
      
      obstaclesRef.current.forEach(obs => {
        ctx.fillStyle = "rgba(201,169,97,0.15)";
        ctx.fillRect(obs.x, 0, 60, obs.top);
        ctx.fillRect(obs.x, canvasHeight.current - obs.bottom, 60, obs.bottom);
      });

      ctx.save();
      ctx.translate(50 + PLANE_SIZE/2, p.y + PLANE_SIZE/2);
      ctx.rotate(p.rotation);
      ctx.fillStyle = "#e4c77a";
      ctx.beginPath(); ctx.moveTo(-15, -5); ctx.lineTo(15, 0); ctx.lineTo(-15, 5); ctx.fill();
      ctx.restore();

      requestRef.current = requestAnimationFrame(loop);
    };

    requestRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState]);

  return (
    <section id={id} className="team-game-section py-24" style={{ background: "#0a0908" }}>
      <div className="container">
        <div className="flex flex-col mb-12 text-center">
          <GameKicker index="05" label="INTERACTIVE SYNERGY" />
          <h2 className="game-title gold-text italic">SYNERGY IN MOTION</h2>
          <p className="game-desc mt-4">Pilot the Meraki Craft through the technical monoliths. Every obstacle cleared strengthens our collective impact.</p>
        </div>

        <div 
          ref={gameContainerRef}
          className="relative w-full aspect-[4/3] md:aspect-video rounded-3xl overflow-hidden bg-black/40 border border-white/10 group cursor-pointer"
          onClick={handleJump}
        >
          <canvas ref={canvasRef} className="w-full h-full" />
          
          <AnimatePresence>
            {gameState === "start" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col items-center justify-center z-30"
              >
                {/* Thumbnail Background */}
                <div 
                  className="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: "url('/images/team/synergy_thumb.png')" }}
                />
                <div className="absolute inset-0 z-10 bg-black/70 backdrop-blur-[2px]" />

                <div className="relative z-20 flex flex-col items-center">
                  <Play className="text-[var(--accentColor)] w-16 h-16 mb-6 cursor-pointer" onClick={initGame} />
                  <h3 className="text-3xl text-white italic">Ready for Synergy?</h3>
                  <button className="btn-primary mt-8" onClick={initGame}>START MISSION</button>
                </div>
              </motion.div>
            )}
            {gameState === "gameOver" && (
              <motion.div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-30">
                <h3 className="text-5xl text-red-500 mb-4">MISSION FAILED</h3>
                <div className="flex gap-8 mb-8">
                  <div className="text-center"><div className="text-xs text-gray-500">SCORE</div><div className="text-3xl text-white">{score}</div></div>
                  <div className="text-center"><div className="text-xs text-gray-500">BEST</div><div className="text-3xl text-[var(--accentColor)]">{highScore}</div></div>
                </div>
                <button className="btn-primary" onClick={initGame}><RotateCcw className="mr-2" /> REBOOT</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <GameInstructionsModal isOpen={showInstructions} onClose={() => setShowInstructions(false)} title="Synergy Mission" instructions={[{title: "Control", desc: "Click or tap to gain altitude. Avoid the monoliths."}]} />
    </section>
  );
};

const GameKicker = ({ index, label }) => (
  <div className="flex items-center justify-center gap-4 mb-6">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--accentColor)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-white/40 uppercase">{label}</span>
  </div>
);

export default TeamGame;
